import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Table, type Column } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CAP, useAutomates, useEnAttente } from "./api";
import type { Automate } from "./types";
import { PageHeader } from "./components/PageHeader";
import { QueryBoundary } from "./components/QueryBoundary";
import { DeviceDetailDrawer } from "./DeviceDetailDrawer";
import { deviceHealth, disciplineLabel, idStr, PROTOCOLE_LABEL } from "./lib";

/**
 * DevicesDashboardPage — the device-status overview: KPI strip + the fleet table with a clear
 * health verdict per analyzer (color + glyph + text). A row opens the detail drawer (test
 * connection, reconfigure, troubleshoot). 5-state DoD via QueryBoundary; the empty state offers the
 * onboarding wizard. Quarantine KPI reads the never-drop worklist.
 */
export function DevicesDashboardPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const canConnect = useCan(CAP.connect);
  const automatesQuery = useAutomates();
  // Quarantine KPI — read-gated; only fetch when the user may read the worklist.
  const canMessageRead = useCan(CAP.messageRead);
  const enAttenteQuery = useEnAttente();
  const quarantaineCount = canMessageRead ? (enAttenteQuery.data?.length ?? null) : null;

  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title={t("lis.dashboard.title")}
        subtitle={t("lis.dashboard.subtitle")}
        actions={
          <Button variant="primary" onClick={() => navigate("/lis/raccorder")}>
            {t("lis.dashboard.add")}
          </Button>
        }
      />

      <QueryBoundary q={automatesQuery}>
        {(automates) => (
          <DashboardBody
            automates={automates}
            quarantaineCount={quarantaineCount}
            canConnect={canConnect}
            onOpen={setOpenId}
            openId={openId}
          />
        )}
      </QueryBoundary>
    </div>
  );
}

function DashboardBody({
  automates,
  quarantaineCount,
  canConnect,
  onOpen,
  openId,
}: {
  automates: Automate[];
  quarantaineCount: number | null;
  canConnect: boolean;
  onOpen: (id: string | null) => void;
  openId: string | null;
}) {
  const { t } = useT();
  const navigate = useNavigate();

  const kpis = useMemo(() => {
    let actifs = 0;
    let attention = 0;
    for (const a of automates) {
      const h = deviceHealth(a).health;
      if (h === "actif") actifs += 1;
      else attention += 1;
    }
    return { total: automates.length, actifs, attention };
  }, [automates]);

  const selected = openId ? automates.find((a) => idStr(a.id) === openId) ?? null : null;

  const columns: ReadonlyArray<Column<Automate>> = [
    {
      key: "automate",
      header: t("lis.dashboard.col.automate"),
      sortable: true,
      sortAccessor: (a) => a.code,
      cell: (a) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{a.libelle}</span>
          <span className="nv-mono nv-ltr-island" style={{ color: token.slate, fontSize: token.fsCaption }}>
            {a.code} · {a.fabricant} {a.modele}
          </span>
        </div>
      ),
    },
    {
      key: "discipline",
      header: t("lis.dashboard.col.discipline"),
      sortable: true,
      sortAccessor: (a) => a.discipline_code,
      cell: (a) => <Badge tone="neutral">{disciplineLabel(a.discipline_code)}</Badge>,
    },
    {
      key: "liaison",
      header: t("lis.dashboard.col.liaison"),
      cell: (a) =>
        a.config ? (
          <span style={{ display: "flex", gap: token.s1, flexWrap: "wrap" }}>
            <Badge tone="info">{PROTOCOLE_LABEL[a.config.protocole]}</Badge>
            <Badge tone="neutral">{t(`lis.transport.${a.config.transport}`)}</Badge>
          </span>
        ) : (
          <span style={{ color: token.steel }}>{t("lis.na")}</span>
        ),
    },
    {
      key: "sante",
      header: t("lis.dashboard.col.sante"),
      sortable: true,
      sortAccessor: (a) => deviceHealth(a).health,
      cell: (a) => {
        const h = deviceHealth(a);
        return <Badge tone={h.tone}>{t(h.labelKey)}</Badge>;
      },
    },
    {
      key: "actions",
      header: t("lis.dashboard.col.actions"),
      align: "end",
      cell: (a) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(idStr(a.id));
          }}
        >
          {t("lis.dashboard.detail")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: token.s3,
        }}
      >
        <Kpi label={t("lis.dashboard.kpi.total")} value={kpis.total} glyph="▤" tone="info" />
        <Kpi label={t("lis.dashboard.kpi.actifs")} value={kpis.actifs} glyph="●" tone="success" />
        <Kpi
          label={t("lis.dashboard.kpi.attention")}
          value={kpis.attention}
          glyph="▲"
          tone={kpis.attention > 0 ? "warn" : "neutral"}
        />
        <Kpi
          label={t("lis.dashboard.kpi.quarantaine")}
          value={quarantaineCount ?? t("lis.na")}
          glyph="❚❚"
          tone={quarantaineCount && quarantaineCount > 0 ? "danger" : "neutral"}
          onClick={() => navigate("/lis/flux")}
        />
      </div>

      <Card title={t("lis.dashboard.count", { n: automates.length })} padded={false}>
        <Table<Automate>
          columns={columns}
          rows={automates}
          rowKey={(a) => idStr(a.id)}
          selectedKey={openId}
          onRowClick={(a) => onOpen(idStr(a.id))}
          pageSize={12}
          emptyTitle={t("lis.dashboard.empty.title")}
          emptyDescription={t("lis.dashboard.empty.desc")}
          emptyAction={
            <Button variant="primary" onClick={() => navigate("/lis/raccorder")}>
              {t("lis.dashboard.add")}
            </Button>
          }
          caption={t("lis.dashboard.title")}
        />
      </Card>

      {selected && (
        <DeviceDetailDrawer
          automate={selected}
          canConnect={canConnect}
          onClose={() => onOpen(null)}
        />
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  glyph,
  tone,
  onClick,
}: {
  label: string;
  value: number | string;
  glyph: string;
  tone: "info" | "success" | "warn" | "danger" | "neutral";
  onClick?: () => void;
}) {
  const toneColor: Record<typeof tone, string> = {
    info: token.info,
    success: token.success,
    warn: token.warn,
    danger: token.danger,
    neutral: token.steel,
  };
  const inner = (
    <>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          inlineSize: 36,
          blockSize: 36,
          borderRadius: token.rSm,
          background: token.surface2,
          color: toneColor[tone],
          fontSize: token.fsTitle,
          flex: "0 0 auto",
        }}
      >
        {glyph}
      </span>
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span
          className="nv-mono nv-ltr-island"
          style={{ fontSize: token.fsH2, fontWeight: 700, color: token.ink, lineHeight: 1.1 }}
        >
          {value}
        </span>
        <span style={{ fontSize: token.fsLabel, color: token.slate }}>{label}</span>
      </span>
    </>
  );

  const baseStyle = {
    display: "flex",
    alignItems: "center",
    gap: token.s3,
    padding: token.s4,
    background: token.white,
    border: `1px solid ${token.line}`,
    borderRadius: token.rLg,
    boxShadow: token.e1,
    inlineSize: "100%",
  } as const;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...baseStyle, font: "inherit", textAlign: "start", cursor: "pointer" }}
      >
        {inner}
      </button>
    );
  }
  return <div style={baseStyle}>{inner}</div>;
}
